import React from "react";
import { Typography, Tooltip, Space, theme, Flex } from "antd";
import { WarningOutlined } from "@ant-design/icons";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  LabelList,
} from "recharts";
import { CHART_COLORS } from "../../../../StatsAndAnalytics/utils/chartColors";
import "./index.scss";

const { Text } = Typography;
const { useToken } = theme;

export interface BottleneckItem {
  id: string | number;
  label: string;
  rate: number; // 0-100 percentage
  count?: {
    success: number; // In failure mode, this is the failure count
    total: number;
  };
  isCritical?: boolean;
}

interface BottleneckMiniChartProps {
  title?: string;
  items: BottleneckItem[];
  emptyText?: string;
  themeColor?: string;
  onItemClick?: (item: BottleneckItem) => void;
  /**
   * Mode determines how colors are applied:
   * - "success": Higher rate = better (green), lower = worse (red) - default
   * - "failure": All bars shown in error colors (we're highlighting problems)
   */
  mode?: "success" | "failure";
  /**
   * Label for the rate in tooltip
   */
  rateLabel?: string;
  /**
   * Label for the count in tooltip
   */
  countLabel?: string;
  /**
   * Total evaluated count - used to set X-axis max so bar length shows proportion
   */
  totalEvaluated?: number;
}

// Color functions based on mode using CHART_COLORS
const getSuccessBarColor = (rate: number): string => {
  if (rate >= 70) return CHART_COLORS.success;
  if (rate >= 50) return CHART_COLORS.warning;
  return CHART_COLORS.error;
};

// For failure mode: All bars are error/danger colors since we're showing problems
const getFailureBarColor = (rate: number): string => {
  if (rate >= 50) return CHART_COLORS.error;
  if (rate >= 30) return CHART_COLORS.warning;
  return CHART_COLORS.error;
};

// Custom tooltip component using Ant Design
const CustomTooltip = ({ 
  active, 
  payload, 
  mode, 
  rateLabel, 
  countLabel 
}: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    
    return (
      <div className="bottleneck-tooltip">
        <Text strong style={{ fontSize: 12 }}>{data.label}</Text>
        <Space direction="vertical" size={2} style={{ marginTop: 4 }}>
          <Text type={mode === "failure" ? "danger" : "success"} style={{ fontSize: 11 }}>
            {data.count?.success || 0} {countLabel || (mode === "failure" ? "failed" : "matched")} out of {data.count?.total || 0} profiles
          </Text>
          <Text type="secondary" style={{ fontSize: 11 }}>
            ({data.rate}% {rateLabel || (mode === "failure" ? "failure rate" : "match rate")})
          </Text>
        </Space>
      </div>
    );
  }
  return null;
};

const BottleneckMiniChart: React.FC<BottleneckMiniChartProps> = ({
  title,
  items,
  emptyText = "No bottlenecks detected",
  themeColor,
  onItemClick,
  mode = "success",
  rateLabel,
  countLabel,
  totalEvaluated,
}) => {
  const { token } = useToken();
  
  if (!items || items.length === 0) {
    return (
      <div className="bottleneck-mini-chart empty">
        <Text type="secondary">{emptyText}</Text>
      </div>
    );
  }

  const colorFn = mode === "failure" ? getFailureBarColor : getSuccessBarColor;

  // X-axis max: Use totalEvaluated if provided, otherwise fall back to max count
  // This ensures bar length represents proportion of total evaluated
  const xAxisMax = totalEvaluated || Math.max(...items.map(item => item.count?.total || item.count?.success || 0), 1);

  // Prepare data for Recharts (reverse for bottom-to-top display)
  // Use actual count instead of percentage
  const chartData = [...items].reverse().map((item) => ({
    ...item,
    name: item.label,
    displayValue: item.count?.success || 0, // Actual count for display
  }));

  const chartHeight = Math.max(items.length * 40 + 20, 100);

  // Custom Y-axis tick component
  const CustomYAxisTick = ({ x, y, payload }: any) => {
    const item = items.find((i: BottleneckItem) => i.label === payload.value);
    const isCritical = mode === "failure" 
      ? (item?.rate ?? 0) >= 50 
      : (item?.rate ?? 0) < 50;
    
    return (
      <g 
        transform={`translate(${x},${y})`}
        style={{ cursor: onItemClick ? "pointer" : "default" }}
        onClick={() => item && onItemClick?.(item)}
      >
        {isCritical && (
          <text x={-8} y={4} fontSize={10} fill={CHART_COLORS.error}>⚠</text>
        )}
        <text
          x={isCritical ? -12 : 0}
          y={4}
          textAnchor="end"
          fontSize={11}
          fill={token.colorTextSecondary}
        >
          {payload.value.length > 25 ? payload.value.substring(0, 25) + "..." : payload.value}
        </text>
      </g>
    );
  };

  return (
    <div className="bottleneck-mini-chart">
      {title && (
        <Flex gap={8} align="center" justify="center" className="chart-title">
          <WarningOutlined style={{ color: CHART_COLORS.error }} />
          <Text style={{ fontSize: 12, fontWeight: 400 , marginLeft: 8}} type="danger">{title}</Text>
        </Flex>
      )}
      <div className="chart-container" style={{ height: chartHeight }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 5, right: 50, left: 120, bottom: 5 }}
            barCategoryGap="20%"
          >
            <XAxis
              type="number"
              domain={[0, xAxisMax]}
              tick={{ fontSize: 10, fill: token.colorTextSecondary }}
              axisLine={{ stroke: token.colorBorderSecondary }}
              tickLine={false}
              label={totalEvaluated ? { 
                value: `of ${totalEvaluated} evaluated`, 
                position: 'insideBottomRight', 
                offset: 0,
                style: { fontSize: 10, fill: token.colorTextSecondary }
              } : undefined}
            />
            <YAxis
              type="category"
              dataKey="label"
              width={115}
              tick={(props) => <CustomYAxisTick {...props} />}
              axisLine={false}
              tickLine={false}
            />
            <RechartsTooltip 
              content={<CustomTooltip mode={mode} rateLabel={rateLabel} countLabel={countLabel} />} 
              cursor={{ fill: token.colorFillTertiary }} 
            />
            <Bar
              dataKey="displayValue"
              radius={[0, 4, 4, 0]}
              onClick={(data: any) => {
                const item = items.find((i) => i.label === data?.label);
                if (item && onItemClick) onItemClick(item);
              }}
              style={{ cursor: onItemClick ? "pointer" : "default" }}
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={colorFn(entry.rate)}
                />
              ))}
              <LabelList
                dataKey="displayValue"
                position="right"
                formatter={(value: any) => `${value}`}
                style={{ fontSize: 11, fontWeight: 600 }}
                fill={token.colorTextSecondary}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default BottleneckMiniChart;
